import React, { useEffect, useState } from 'react';

type DarkTheme = 'dark-blue' | 'dark-black' | 'dark-gray';
type ThemeMode = DarkTheme | 'light';
type ThemeToggleVariant = 'default' | 'menu';

interface ThemeToggleProps {
  className?: string;
  variant?: ThemeToggleVariant;
}

const DARK_THEMES: DarkTheme[] = ['dark-blue', 'dark-black', 'dark-gray'];
const DARK_THEME_OPTIONS: Array<{ value: DarkTheme; label: string }> = [
  { value: 'dark-blue', label: 'Xanh' },
  { value: 'dark-black', label: 'Đen' },
  { value: 'dark-gray', label: 'Xám' },
];
const THEME_EVENT = 'mlpay-theme-change';

const getStoredDarkTheme = (): DarkTheme => {
  const stored = localStorage.getItem('darkTheme');
  return DARK_THEMES.includes(stored as DarkTheme)
    ? (stored as DarkTheme)
    : 'dark-black';
};

const getStoredTheme = (): ThemeMode => {
  const stored = localStorage.getItem('theme');
  if (stored === 'light') return 'light';
  if (DARK_THEMES.includes(stored as DarkTheme)) return stored as DarkTheme;
  return getStoredDarkTheme();
};

const applyTheme = (theme: ThemeMode) => {
  document.documentElement.setAttribute('data-theme', theme);
  localStorage.setItem('theme', theme);
  if (theme !== 'light') localStorage.setItem('darkTheme', theme);
  window.dispatchEvent(
    new CustomEvent<ThemeMode>(THEME_EVENT, { detail: theme }),
  );
};

export const ThemeToggle = ({
  className = '',
  variant = 'default',
}: ThemeToggleProps): JSX.Element => {
  const [theme, setTheme] = useState<ThemeMode>(() => getStoredTheme());
  const isDark = theme !== 'light';

  useEffect(() => {
    const initialTheme = getStoredTheme();
    setTheme(initialTheme);
    document.documentElement.setAttribute('data-theme', initialTheme);

    const handleThemeChange = (event: Event) => {
      const nextTheme = (event as CustomEvent<ThemeMode>).detail;
      if (
        nextTheme === 'dark-blue' ||
        nextTheme === 'dark-black' ||
        nextTheme === 'dark-gray' ||
        nextTheme === 'light'
      ) {
        setTheme(nextTheme);
      }
    };

    const handleStorageChange = (event: StorageEvent) => {
      if (event.key === 'theme' || event.key === 'darkTheme') {
        setTheme(getStoredTheme());
      }
    };

    window.addEventListener(THEME_EVENT, handleThemeChange);
    window.addEventListener('storage', handleStorageChange);

    return () => {
      window.removeEventListener(THEME_EVENT, handleThemeChange);
      window.removeEventListener('storage', handleStorageChange);
    };
  }, []);

  const toggleTheme = () => {
    const nextTheme: ThemeMode = isDark ? 'light' : getStoredDarkTheme();
    setTheme(nextTheme);
    applyTheme(nextTheme);
  };

  const selectDarkTheme = (nextTheme: DarkTheme) => {
    setTheme(nextTheme);
    applyTheme(nextTheme);
  };

  const icon = isDark ? (
    <svg viewBox='0 0 24 24' fill='none' aria-hidden='true'>
      <circle cx='12' cy='12' r='4' fill='currentColor' />
      <path
        d='M12 2v2m0 16v2M4.22 4.22l1.42 1.42m12.72 12.72 1.42 1.42M2 12h2m16 0h2M4.22 19.78l1.42-1.42M18.36 5.64l1.42-1.42'
        stroke='currentColor'
        strokeWidth='2'
        strokeLinecap='round'
      />
    </svg>
  ) : (
    <svg viewBox='0 0 24 24' fill='none' aria-hidden='true'>
      <path
        d='M21 12.79A9 9 0 1 1 11.21 3a7 7 0 0 0 9.79 9.79z'
        fill='currentColor'
      />
    </svg>
  );

  if (variant === 'menu') {
    return (
      <div
        className={`theme-menu ${className}`.trim()}
        onClick={(event) => event.stopPropagation()}
      >
        <button
          type='button'
          className='theme-toggle theme-toggle--menu'
          onClick={toggleTheme}
          title={isDark ? 'Chuyển sang Light Mode' : 'Chuyển sang Dark Mode'}
          aria-label={
            isDark ? 'Chuyển sang Light Mode' : 'Chuyển sang Dark Mode'
          }
        >
          {icon}
          <span>{isDark ? 'Light Mode' : 'Dark Mode'}</span>
        </button>
        {isDark && (
          <div
            className='theme-palette'
            role='group'
            aria-label='Màu Dark Mode'
          >
            <span className='theme-palette__label'>Màu Dark Mode</span>
            {DARK_THEME_OPTIONS.map((option) => (
              <button
                type='button'
                key={option.value}
                className={`theme-palette__option ${theme === option.value ? 'is-active' : ''}`}
                onClick={() => selectDarkTheme(option.value)}
                aria-pressed={theme === option.value}
              >
                <span
                  className={`theme-palette__swatch theme-palette__swatch--${option.value}`}
                />
                {option.label}
              </button>
            ))}
          </div>
        )}
      </div>
    );
  }

  return (
    <button
      type='button'
      className={`theme-toggle theme-toggle--${variant} ${className}`.trim()}
      onClick={toggleTheme}
      title={isDark ? 'Chuyển sang Light Mode' : 'Chuyển sang Dark Mode'}
      aria-label={isDark ? 'Chuyển sang Light Mode' : 'Chuyển sang Dark Mode'}
    >
      {icon}
    </button>
  );
};
